import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import useContractsTable from '../../ContractsTable/useContractsTable'
import useContractsPagination from '../../ContractsPagination/useContractsPagination'
import { selectError } from '../../DashboardV2Selectors'

export type ContractsBodyState = 'loading' | 'error' | 'empty' | 'rows'

const SORTABLE_COLUMNS = [
  { key: 'status', labelKey: 'contractsTable:status' },
  { key: 'dateCreatedAt', labelKey: 'contractsTable:dates' },
  { key: 'lastname', labelKey: 'contractsTable:contact' },
  { key: 'providerName', labelKey: 'contractsTable:offer' },
]

const resolveBodyState = (isLoading: boolean, error: string | null, count: number): ContractsBodyState => {
  if (isLoading) return 'loading'
  if (error) return 'error'
  if (count === 0) return 'empty'
  return 'rows'
}

interface UseContractsCardParams {
  businessType: string
  getContracts: () => void
}

const useContractsCard = ({ businessType, getContracts }: UseContractsCardParams) => {
  const { t } = useTranslation()
  const { sortedContracts, isLoading, sort, handleSort } = useContractsTable(businessType)
  const error = useSelector(selectError)
  const { pagination, goToNextPage, goToPreviousPage, hasNext, hasPrevious } = useContractsPagination()
  const bodyState = resolveBodyState(isLoading, error, sortedContracts.length)

  return {
    title: t('dashboard:overview.contracts_title'),
    actionsLabel: t('contractsTable:actions'),
    columns: SORTABLE_COLUMNS.map(column => ({
      key: column.key,
      label: t(column.labelKey),
      direction: sort.key === column.key ? sort.direction : null,
      onSort: () => handleSort(column.key),
    })),
    contracts: sortedContracts,
    bodyState,
    pager: {
      label: t('dashboard:overview.pagination'),
      pageLabel: t('dashboard:overview.page', { page: pagination.currentPage }),
      previousLabel: t('common:previous'),
      nextLabel: t('common:next'),
      hasPrevious,
      hasNext,
      onPrevious: goToPreviousPage,
      onNext: goToNextPage,
    },
    showBottomPager: bodyState === 'rows',
    onRetry: getContracts,
  }
}

export default useContractsCard
